import React, { useEffect, useState } from "react";
import {
  Button,
  Spinner,
} from "@fluentui/react-components";
import { useMsal } from "@azure/msal-react";
import { useTranslation } from "react-i18next";
import { Dismiss20Regular, Save20Regular } from "@fluentui/react-icons";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { getAccessToken } from "../../Services/GraphServices";
import {
  createWorkflow,
  updateWorkflow,
  getWorkflow,
} from "../../Services/HeedifyServices";
import "../CSS/WorkflowsComponent.css";
import WorkflowForm from "./WorkflowForm";
import { useAbility } from "../../Contexts/AbilityContext";
import { ReadOnlyFieldset } from "../GenericComponent/ReadOnlyFieldset";
import { useNotification } from '../GenericComponent/NotificationSystem';

export default function WorkflowsComponent(props) {
  const { instance, accounts } = useMsal();
  const activeAccount = accounts[0];
  const userId = activeAccount?.localAccountId;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { workflowId } = useParams();
  const ability = useAbility();
  const { showSuccess, showError } = useNotification();

  const isEditMode = workflowId && workflowId !== "add";
  const isCreateMode = !isEditMode;

  const [workflow, setWorkflow] = useState({
    scope: "",
    triggerEvent: "",
    dataQuery: {
      url: "",
      method: "POST",
      headers: {},
      mapping: {},
    },
    webhook: {
      url: "",
      method: "POST",
      headers: {},
      body: {},
    },
  });

  const [formValid, setFormValid] = useState(false);

  // Permission check
  const canCreate = props.SCOPES_NAMES?.some((scope) =>
    ability?.can("create", `Flow.main@${scope}`)
  );
  const canUpdate =
    isEditMode && workflow.scope && ability?.can("update", `Flow.main@${workflow.scope}`);
  // L'écran s'ouvre désormais sur `canRead` (cf. la liste) : il faut donc qu'il sache se rendre
  // sans droit d'écriture, au lieu d'offrir un formulaire dont l'enregistrement finirait en 403.
  const readOnly = isEditMode && !canUpdate;

  useEffect(() => {
    if (isEditMode) {
      // Mode Edit: charger depuis location.state ou API
      if (location.state?.workflow) {
        setWorkflow(location.state.workflow);
      } else {
        fetchWorkflow();
      }
    }
  }, [workflowId]);

  const fetchWorkflow = async () => {
    if (!workflowId || !props.ACTIVE_TENANT) return;

  
    try {
      const token = await getAccessToken(instance, activeAccount);
      // On ne connaît pas le scope, donc chercher dans tous les scopes
      for (const scope of props.SCOPES_NAMES || []) {
        const result = await getWorkflow(props.ACTIVE_TENANT, scope, workflowId, token);
        if (result) {
          setWorkflow(result);
          break;
        }
      }
    } catch (error) {
      console.error("Error fetching workflow:", error);
      showError(t("ERROR_FETCHING_WORKFLOW"), error?.message);
    } 
  };

  const handleSubmit = async () => {
    if (!formValid) {
      showError(t("PLEASE_FILL_REQUIRED_FIELDS"));
      return;
    }

    if (isCreateMode && !canCreate) {
      showError(t("NO_PERMISSION_CREATE"));
      return;
    }

    if (isEditMode && !canUpdate) {
      showError(t("NO_PERMISSION_UPDATE"));
      return;
    }

   
    try {
      const token = await getAccessToken(instance, activeAccount);

      if (isCreateMode) {
        const result = await createWorkflow(
          props.ACTIVE_TENANT,
          workflow.scope,
          workflow,
          token
        );

        if (result.success && result.message === "created") {
          // Mettre à jour le store Redux avec le nouveau workflow
          if (result.data && props.setWorkflows && props.WORKFLOWS) {
            props.setWorkflows([...props.WORKFLOWS, result.data]);
          }
          showSuccess(t("WORKFLOW_CREATED_SUCCESS"));
          navigate("/workflows");
        } else {
          const errorMsg = result.message || "Erreur inconnue";
          showError(t("WORKFLOW_CREATION_FAILED"), errorMsg);
        }
      } else {
        const result = await updateWorkflow(
          props.ACTIVE_TENANT,
          workflow.scope,
          workflowId,
          workflow,
          token
        );

        if (result.success && result.message === "updated") {
          // Mettre à jour le store Redux avec le workflow modifié
          if (result.data && props.setWorkflows && props.WORKFLOWS) {
            props.setWorkflows(
              props.WORKFLOWS.map((w) => (w.id === workflowId ? result.data : w))
            );
          }
          showSuccess(t("WORKFLOW_UPDATED_SUCCESS"));
          navigate("/workflows");
        } else {
          const errorMsg = result.message || "Erreur inconnue";
          showError(t("WORKFLOW_UPDATE_FAILED"), errorMsg);
        }
      }
    } catch (error) {
      console.error("Error saving workflow:", error);
      showError(isEditMode ? t("WORKFLOW_UPDATE_ERROR") : t("WORKFLOW_CREATION_ERROR"), error?.message);
    } 
  };

  const handleCancel = () => {
    navigate("/workflows");
  };

  

  return (
    <div className="workflows-component-container">
      <div className="action-buttons-workflow">
        <Button
          appearance="secondary"
          icon={<Dismiss20Regular />}
          onClick={handleCancel}
        >
          {t("cancel")}
        </Button>
      {/* Lecture seule : on CONSULTE sans pouvoir enregistrer. Le bouton est MASQUÉ et non
          grisé — grisé, il laisse croire qu'il manque une saisie ; absent, il dit qu'il n'y a
          rien à enregistrer ici. */}
      {!readOnly && (
        <Button
          appearance="primary"
          icon={<Save20Regular />}
          onClick={handleSubmit}
          disabled={!formValid}
        >
          {t("save")}
        </Button>
      )}
      </div>

      <ReadOnlyFieldset readOnly={readOnly}>
        <WorkflowForm
          workflow={workflow}
          setWorkflow={setWorkflow}
          isUpdate={isEditMode}
          setFormValid={setFormValid}
          SCOPES_NAMES={props.SCOPES_NAMES}
        />
      </ReadOnlyFieldset>
    </div>
  );
}
